import React, { useState, useEffect } from "react";
import { changePassword } from '../../../service/userService';
import { ArrowLeft, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";

const ChangePassword = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: "", message: "" });
    const [userId, setUserId] = useState("");
  
    useEffect(() => {
      try {
        const storedUserData = localStorage.getItem("user");
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          const id = userData?._id || userData?.data?._id || userData?.id || userData?.data?.id;
          if (id) {
            setUserId(id);
          } else {
            console.error("Could not find user ID in stored data");
            setStatusMessage({ 
              type: "error", 
              message: "User ID not found. Please log in again." 
            });
          }
        } else {
          console.error("No user data found in localStorage");
          setStatusMessage({ 
            type: "error", 
            message: "Please log in to change your password." 
          });
        }
      } catch (error) {
        console.error("Error getting user ID:", error);
        setStatusMessage({ 
          type: "error", 
          message: "Error accessing user data. Please try logging in again." 
        });
      }
    }, []);
  
    const validateForm = () => {
      const newErrors = {};
      
      if (!currentPassword) {
        newErrors.currentPassword = "Current password is required";
      }
      
      if (!newPassword) {
        newErrors.newPassword = "New password is required";
      }  else if (!/[A-Z]/.test(newPassword)) {
        newErrors.newPassword = "Password must contain at least one uppercase letter";
      } else if (!/[a-z]/.test(newPassword)) {
        newErrors.newPassword = "Password must contain at least one lowercase letter";
      } else if (!/[0-9]/.test(newPassword)) {
        newErrors.newPassword = "Password must contain at least one number";
      } else if (!/[!@#$%^&*]/.test(newPassword)) {
        newErrors.newPassword = "Password must contain at least one special character (!@#$%^&*)";
      }
      
      if (!confirmPassword) {
        newErrors.confirmPassword = "Please confirm your new password";
      } else if (newPassword !== confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };
  
    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatusMessage({ type: "", message: "" });
      
        if (validateForm()) {
          setIsSubmitting(true);
          try {
            if (!userId) throw new Error("User ID not found. Please log in again.");
      
            const passwordData = {
              id: userId,
              password: currentPassword, 
              newPassword: newPassword, 
            };
      
            const response = await changePassword(passwordData);
            setStatusMessage({ 
              type: "success", 
              message: response.message || "Password changed successfully!" 
            });
      
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setErrors({});
          } catch (error) {
            setStatusMessage({ 
              type: "error", 
              message: error.message || "Failed to change password. Please try again." 
            });
          } finally {
            setIsSubmitting(false);
          }
        }
      };
  
    const togglePasswordVisibility = (field) => {
      switch (field) {
        case "current":
          setShowCurrentPassword(!showCurrentPassword);
          break;
        case "new":
          setShowNewPassword(!showNewPassword);
          break;
        case "confirm":
          setShowConfirmPassword(!showConfirmPassword);
          break;
        default:
          break;
      }
    };
  
    const handleCancel = () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
      setStatusMessage({ type: "", message: "" });
    };
  
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Change Password</h4>
                </div>
              </div>
            </div>
          </div>
  
          <div className="password-card">
            <div className="password-card-body">
              {statusMessage.message && (
                <div className={`password-message ${
                  statusMessage.type === "error" ? "password-error-alert" : "password-success-alert"
                }`}>
                  {statusMessage.type === "error" ? (
                    <AlertCircle size={20} />
                  ) : (
                    <CheckCircle size={20} />
                  )}
                  <span>{statusMessage.message}</span>
                </div>
              )}
  
              <form onSubmit={handleSubmit}>
                <div className="password-form-container">
                  <div className="password-form-row">
                    <div className="password-form-group">
                      <label className="password-form-label">Current Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          className={`password-form-control ${
                            errors.currentPassword ? "password-input-error" : ""
                          }`}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => togglePasswordVisibility("current")}
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.currentPassword && (
                        <div className="password-error-message">{errors.currentPassword}</div>
                      )}
                    </div>
  
                    <div className="password-form-group">
                      <label className="password-form-label">New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          className={`password-form-control ${
                            errors.newPassword ? "password-input-error" : ""
                          }`}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => togglePasswordVisibility("new")}
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <div className="password-error-message">{errors.newPassword}</div>
                      )}
                    </div>
  
                    <div className="password-form-group">
                      <label className="password-form-label">Confirm New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          className={`password-form-control ${
                            errors.confirmPassword ? "password-input-error" : ""
                          }`}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => togglePasswordVisibility("confirm")}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <div className="password-error-message">{errors.confirmPassword}</div>
                      )}
                    </div>
                  </div>
  
                  <div className="password-button-row">
                    <button type="submit" className="password-submit-btn" disabled={isSubmitting}>
                      {isSubmitting ? "Changing..." : "Change Password"}
                    </button>
                    <button type="button" className="password-cancel-btn" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  export default ChangePassword;